import * as React from "react";
import { useSelector } from "react-redux";

import { DEFAULT_NATIVE_WIDTH } from "~constants";
import ModuleContext from "~components/modules/module/modulecontext";
import PlayIcon from "~components/shared/play-icon/PlayIcon";
import { isPrioritySelector } from "~redux/selectors";
import type { Image as LineupImage } from "~types/types";
import {
    makeRetinaSrcsets,
    getHeightFromWidthAndAspectRatio,
    makeFallbackImage,
} from "~utils";
import { formatImageCaption } from "~utils/replaceCaption";
import stripMarkup from "~utils/stripMarkup";

import styles from "./styles.module.css";

interface LazyImageProps {
    altFallback?: string;
    aspectRatio?: number;
    contentType?: string;
    height?: number;
    image: LineupImage | undefined;
    isLazy?: boolean;
    width?: number;
    className?: string;
    desktopMaxWidth?: number;
    mobileMaxWidth?: number;
    showCaption?: boolean;
}

const DEFAULT_DESKTOP_WIDTH = DEFAULT_NATIVE_WIDTH;
const DEFAULT_MOBILE_WIDTH = DEFAULT_DESKTOP_WIDTH / 2;

export default function LazyRetinaImage({
    altFallback,
    aspectRatio,
    contentType,
    image,
    className,
    desktopMaxWidth = DEFAULT_DESKTOP_WIDTH,
    mobileMaxWidth = DEFAULT_MOBILE_WIDTH,
    showCaption = false,
}: LazyImageProps) {
    const { baseUrl = "", alt, credit } = image || {};

    const altText = alt || altFallback;

    const styleObject = {
        "--aspect-ratio": aspectRatio && Number(aspectRatio?.toFixed(2)),
    } as React.CSSProperties;

    const { mobile: mobileSrcSet, desktop: desktopSrcSet } =
        makeRetinaSrcsets({
            baseUrl,
            desktopMaxWidth,
            mobileMaxWidth,
            aspectRatio,
        }) || {};

    const mobileMaxHeight = getHeightFromWidthAndAspectRatio(
        mobileMaxWidth,
        aspectRatio
    );

    const replacedCaption = formatImageCaption(credit || "");

    const { componentName } = React.useContext(ModuleContext);
    const isPriority = useSelector((state) =>
        isPrioritySelector(state, componentName)
    );

    return (
        <figure data-component="lazy-image" className={className}>
            <picture className={styles.lazy} style={styleObject}>
                {contentType === "relatedWithPlayIcon" && <PlayIcon />}
                <source
                    media="screen and (max-width: 759px)"
                    srcSet={mobileSrcSet ?? ""}
                />
                <source
                    media="screen and (min-width: 759px)"
                    srcSet={desktopSrcSet ?? ""}
                />
                <img
                    src={makeFallbackImage(
                        baseUrl,
                        mobileMaxWidth,
                        mobileMaxHeight
                    )}
                    data-component="image"
                    alt={altText}
                    loading={isPriority ? "eager" : "lazy"}
                />
            </picture>
            {showCaption && Boolean(credit) && (
                <figcaption
                    className={styles.caption}
                    data-component="lazy-image-caption"
                >
                    {stripMarkup(replacedCaption)}
                </figcaption>
            )}
        </figure>
    );
}
